import { useState, useEffect, useContext } from 'react';
import { Paper, Box, Alert } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppContext, SttTable, SttTopControls } from '../../../sporttia/all';
import { updateElementInArray } from '../../../lib/utils';
import { useToggle } from '../../../lib/hooks';
import FileCSVLineDialog from './FileCSVLineDialog';
import ImportUsersDialog from './ImportUsersDialog';
import { PERMISSIONS } from '../../../lib/permissions';

export default function FileCSV(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const [lines, setLines] = useState<any>();
    const [filters, setFilters] = useState<{
        searchingString: string;
        status: string | null;
    }>({
        searchingString: '',
        status: null,
    });
    const [selectedLine, setSelectedLine] = useState<any>(null);
    const [file, setFile] = useState<any>();
    const [openImportUsers, setOpenImportUsers] = useState<boolean>(false);

    // Trash hook
    const [trash, toggleTrash] = useToggle(() => {
        loadLines();
    });

    const loadLines = (p = { page: 1, rows: 40 }) => {
        cxt?.api('GET', `/files/csvs/${id}/lines`, {
            params: {
                ...filters,

                trash,
                page: p.page,
                rows: p.rows,
            },
            success: (r: any) => {
                setLines(r);
            },
        });
    };

    const loadFile = () => {
        cxt?.api('GET', `/files/${id}`, {
            success: (r: any) => {
                setFile(r.file);
                cxt?.setHeader({
                    title: r.file.name,
                });
                loadLines();
            },
        });
    };

    // File id changes
    useEffect(() => {
        if (id) loadFile();
    }, [id]);

    // Get field columns
    const getFieldColumns = () =>
        [...Array(cxt?.constants?.numFieldsToUserImport || 0).keys()].map(
            (num: any) => ({
                title: `#${num}`,
                field: `field_${num}`,
            }),
        );

    // Get icon status
    const getIconStatus = (line: any) => {
        if (line.isImported) {
            return (
                <CheckCircleOutlineIcon style={{ color: 'var(--ok-ink)' }} />
            );
        }
        if (line.error) {
            return <HighlightOffIcon style={{ color: '#df3525' }} />;
        }
        return null;
    };

    // -----| Render |-----
    return (
        <Paper>
            <SttTopControls
                p={2}
                fields={
                    [
                        {
                            caption: t('Name'),
                            name: 'searchingString',
                            type: 'text',
                            value: filters.searchingString,
                            onEnterKey: loadLines,
                        },
                    ] as any
                }
                extraFields={
                    [
                        {
                            md: 5,
                            sm: 12,
                            xs: 12,
                            caption: t('Status'),
                            name: 'status',
                            value: filters.status ?? '',
                            type: 'select',
                            cleanable: true,
                            options: [
                                {
                                    value: 'ERROR',
                                    caption: t('OnlyErrors'),
                                },
                                {
                                    value: 'NOT_EXECUTED',
                                    caption: t('OnlyNotExecuted'),
                                },
                            ],
                        },
                    ] as any
                }
                mainAction={{
                    type: 'create',
                    permission: PERMISSIONS.CUSTOMER.CREATE,
                    caption: t('ImportUsers'),
                    onClick: () => setOpenImportUsers(true),
                }}
                onChange={({
                    name,
                    value,
                }: {
                    name: string | undefined;
                    value: unknown;
                }) => {
                    if (!name) return;
                    setFilters({ ...filters, [name]: value });
                }}
                onFilter={loadLines}
                onToggleTrash={toggleTrash}
            />

            {file && (file.numErrors > 0 || file.numImported > 0) && (
                <Box p={2}>
                    <Alert severity="info" variant="filled">
                        {file.numImported > 0 && (
                            <Box>
                                {t('Imported')}: {file.numImported}
                            </Box>
                        )}

                        {file.numErrors > 0 && (
                            <Box>
                                {t('Errors')}: {file.numErrors}
                            </Box>
                        )}
                    </Alert>
                </Box>
            )}

            <SttTable
                data={lines}
                autoload={false}
                columns={[
                    {
                        title: t('Status'),
                        value: (row: any) => getIconStatus(row),
                    },
                    ...getFieldColumns(),
                ]}
                onFetch={loadLines}
                onClickRow={(row: any) => setSelectedLine(row)}
            />

            <FileCSVLineDialog
                line={selectedLine}
                onClose={() => setSelectedLine(null)}
                onUpdate={(newLine: any) => {
                    setLines({
                        ...lines,
                        rows: updateElementInArray(lines.rows, newLine),
                    });
                    setSelectedLine(null);
                }}
            />

            <ImportUsersDialog
                open={openImportUsers}
                file={file}
                lines={lines}
                onClose={() => setOpenImportUsers(false)}
                onUpdate={(newFileCSV: any) => {
                    setFile(newFileCSV);
                    loadLines();
                }}
            />
        </Paper>
    );
}
